// BM-H4222 -- guidance and field hints for the Bonsai Pen SHA wizard.
//
// Drawn from the company law traps in founder shareholders' agreements
// (training note, 11 October 2026; v2 of 15 October 2026, BM-H4367). Each
// step gets a short "what to watch" box; fields get a one-line hint under the
// label and the same text as a hover tooltip. Wording is for the fee earner,
// not the client.

export const STEP_GUIDANCE = {
  1: {
    title: "Before you start",
    points: [
      "Check the registered articles first. Pre-October 2009 companies may still run on Table A 1985, which gives the chair a casting vote at board and possibly general meetings.",
      "The agreement does not change the articles (CA 2006 s 33). Tell the client so; recommend amended articles if the leaver and transfer terms must bind the Company and outsiders.",
      "Cross-option: embedded puts the deed in Schedule 4 of one document; standalone produces the agreement and a separate deed in the same Word file, after a page break.",
      "The draft opens with a drafter's checklist page and a signing pack (members' resolution and board minute for the Company entering the agreement). Work through them and take them out before sending.",
    ],
  },
  3: {
    title: "Shareholders",
    points: [
      "Every shareholder signs as a deed in front of an independent adult witness. The powers of attorney in the agreement depend on it being a deed.",
      "Corporate shareholders, trustees or nominees need a different structure (execution, events of default, look-through). Flag them on the checklist.",
    ],
  },
  4: {
    title: "Voting thresholds",
    points: [
      "Approval levels are now set per reserved matter on Step 6. The qualified majority percentage here is what 'Qualified Majority' means in the agreement.",
      "In a 50/50 company any majority test is unanimity in practice. Use Unanimous Consent unless there is a reason not to.",
    ],
  },
  5: {
    title: "Directors",
    points: [
      "A director can always be removed by ordinary resolution with special notice (CA 2006 s 168). The agreement can only govern how shareholders vote.",
      "Director protection falls away for a bad leaver (clause 5.2), so a protected founder cannot block their own removal.",
      "If the articles give the chair a casting vote, clause 5.5 is a promise not to use it. Whether that binds is untested; amending the articles is the safer fix.",
      "A founder removed in breach of the protection clause is not a Leaver unless they choose to be, so the majority cannot use its own breach to buy them out cheaply (Alghussein v Eton College).",
      "Clause 5.6 sets the time each founder-director gives the business, with a removal ground if they persistently fail to give it after written notice. Removal on that ground prices as a good leaver. Each founder should also have a service agreement.",
    ],
  },
  6: {
    title: "Reserved matters",
    points: [
      "Reserved matters are the shareholders' promise about how they vote, not a promise by the Company (Russell v Northern Bank). The text is drafted that way.",
      "Pick an approval level for each matter. Anything left without a level is reserved at Unanimous Consent. 'Not reserved' leaves it out of the list.",
      "Tell the client that once a matter is approved, every shareholder is bound to vote for the resolution that carries it out (clause 9.1). They lose a later free vote on it (CA 2006 s 283).",
      "Do not leave thresholds blank. A blank comes out as £[ ] for you to fill in.",
    ],
  },
  7: {
    title: "Transfers and new issues",
    points: [
      "Pre-emption on transfer is always included, with acceptance dates and prices fixed for every route (agreed, unchallenged, valued, deemed).",
      "Pre-emption on new issues replaces statutory pre-emption. Pass the s 569 written special resolution at signing and file it within 15 days.",
      "The power of attorney to the Company is always included: transfers by a reluctant seller cannot complete without it.",
    ],
  },
  8: {
    title: "Events of default",
    points: [
      "Ceasing to be a director makes a shareholder a Leaver and triggers a deemed transfer (all in, all out). Switch it off only if the client wants a departing founder to keep shares.",
      "With a cross-option, death is dealt with by the deed of cross-option first; the death switch only matters if both options lapse.",
      "Bankruptcy always sells at Fair Value. That answers an undervalue challenge but not s 284 Insolvency Act 1986. The power of attorney is not revoked by bankruptcy (Powers of Attorney Act 1971 s 4(1)), but whether it can be used against shares vested in a trustee in bankruptcy is not settled.",
    ],
  },
  9: {
    title: "Leaver pricing",
    points: [
      "A bad leaver gets the lower of cost and Fair Value. For the breach-type limbs there is a floor of a percentage of Fair Value, so the discount is defensible as a primary obligation (Cavendish v Makdessi).",
      "Nominal value pricing and tiered discounts are retired: they invite a penalty challenge.",
      "Unsold deemed transfer shares can go to a nominee; if nobody buys, the notice lapses rather than leaving the shares in limbo.",
    ],
  },
  10: {
    title: "Completion",
    points: [
      "Completion runs from the end of the acceptance period, so the period you choose here is added to it.",
      "A company buy-back is paid in full at completion (CA 2006 Part 18). Other buyers pay by instalments only if the seller agrees.",
    ],
  },
  11: {
    title: "Drag and tag",
    points: [
      "Drag binds the minority to sell on the same terms. Set the threshold so that no single founder can drag the others.",
      "Tag protects the minority on a change of control. Switching it off removes the clause entirely.",
    ],
  },
  12: {
    title: "Deadlock",
    points: [
      "In a 50/50 company a special resolution cannot break a deadlock. Use mediation or Russian roulette.",
      "Russian roulette favours the founder with more money. Warn the client before choosing it.",
    ],
  },
  13: {
    title: "Restrictive covenants",
    points: [
      "The court will only delete words, not rewrite a covenant (Tillman v Egon Zehnder). Keep each restriction no wider than the business needs.",
      "Twelve months is usual for founders. Longer periods need a reason you can explain to a judge.",
    ],
  },
  14: {
    title: "Cross-option",
    points: [
      "The price is the Agreed Value if it was signed in the 12 months before death, otherwise Fair Value. It does not depend on any insurance paying out.",
      "Policies are arranged through an FCA-authorised IFA, written in trust and paid for personally, not by the Company. Refer; do not advise on the policy.",
      "Diarise a review of the Agreed Value and cover every 12 months.",
    ],
  },
};

export const FIELD_HINTS = {
  existing_sha: "If yes, the new agreement replaces it. Check who signed the old one and that they all sign the new one.",
  has_corporate_shareholders: "Corporate shareholders need their own events of default and execution blocks. Flag for review.",
  qualified_majority_pct: "Used wherever a reserved matter is set to Qualified Majority.",
  min_pct_appoint_director: "Director model A only: the holding needed to appoint a director.",
  all_in_all_out: "Yes: a shareholder who sells must sell every share.",
  transfer_acceptance_days: "Business Days from the Acceptance Date for the others to take up the shares.",
  retain_voting_during_transfer: "No: shares under a deemed transfer lose votes and dividends until they are sold.",
  company_purchase_option: "The Company may buy shares the others do not take up, if it lawfully can (CA 2006 Part 18).",
  preemption_new_issues: "Yes replaces statutory pre-emption (ss 561 and 562) with a contractual right.",
  default_cease_director: "Trap 5: without this, a founder can stop working and keep their shares.",
  time_commitment_clause: "Trap 18: without it there is no contractual answer to a co-founder who stops pulling their weight but does not resign.",
  time_commitment: "What each founder-director gives the business, in words. Left blank, the draft shows a placeholder.",
  time_notice_days: "Days the failure must continue after written notice before the others can remove them. 30 is the starting point.",
  default_death: "With a cross-option the deed deals with death first; this only decides whether the shares must be sold if both options lapse.",
  bad_leaver_discount: "No: every leaver sells at Fair Value.",
  bad_leaver_floor_pct: "Minimum price for a bad leaver whose fault is a breach (paragraphs b to d of the definition). 50% is the starting point.",
  good_leaver_option: "B is all in, all out. A lets the other shareholders decide whether a good leaver must sell.",
  completion_days: "Business Days after the acceptance period ends.",
  late_interest_rate: "Percentage above Bank of England base rate on late payment.",
  drag_threshold_pct: "Holders of at least this % of the shares can drag the rest.",
  tag_trigger_pct: "A sale giving a buyer more than this % triggers tag.",
  deadlock_third_party: "Name an agreed mediator if the founders already have one in mind.",
  non_compete_months: "Months after the shareholder ceases to hold shares.",
  geographic_scope: "Keep it to where the business actually trades.",
  restrictions_during_detail: "Not added to the text automatically. Put anything specific in Additional instructions as well.",
  life_assurance: "Cover can be arranged after signing. The deed requires each shareholder to apply.",
  shareholder_values: "Becomes each shareholder's Cover Amount in Appendix 3. Confirm with the IFA.",
  call_option_months: "Runs from the date of the grant of probate or letters of administration, not from death.",
  put_option_months: "Starts the day after the call period ends.",
  terminate_other: "Not added to the text automatically. Mention it in Additional instructions.",
};

// Approval levels offered per reserved matter (trap 3).
export const APPROVAL_LEVELS = [
  { value: "unanimous", label: "Unanimous Consent" },
  { value: "qualified", label: "Qualified Majority" },
  { value: "simple", label: "Simple Majority" },
  { value: "not_reserved", label: "Not reserved" },
];
